/**
 * ImportDraftsModal — Sprint 5
 *
 * Liste et validation des brouillons créés par ImportGeneralIAModal.
 * Pour chaque brouillon : voir les lignes, cocher, choisir parcelle cible, valider ou rejeter.
 */
import { useEffect, useState, useCallback } from "react";
import { X, Loader2, CheckCircle2, Trash2, ChevronRight, Sparkles, FileText, AlertTriangle } from "lucide-react";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DOC_TYPE_LABELS = {
  facture_phyto: { label: "Facture phyto", color: "#059669", icon: "📦" },
  bon_traitement: { label: "Bon de traitement", color: "#0891B2", icon: "💧" },
  analyse_sol: { label: "Analyse de sol", color: "#7C3AED", icon: "🧪" },
  inconnu: { label: "Non classé", color: "#B45309", icon: "❓" },
};

export default function ImportDraftsModal({ onClose, onValidated, focusDraftId }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [parcels, setParcels] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, pRes] = await Promise.all([
        authFetch(`${API}/import/drafts`),
        authFetch(`${API}/parcels/followed`),
      ]);
      if (dRes.ok) {
        const data = await dRes.json();
        setDrafts(data.drafts || []);
        if (focusDraftId) {
          const target = (data.drafts || []).find((d) => d.draft_id === focusDraftId);
          if (target) setSelected(target);
        }
      }
      if (pRes.ok) {
        const p = await pRes.json();
        setParcels(Array.isArray(p) ? p : (p.parcels || []));
      }
    } finally {
      setLoading(false);
    }
  }, [focusDraftId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (draftId) => {
    if (!window.confirm("Supprimer définitivement ce brouillon ?")) return;
    const res = await authFetch(`${API}/import/drafts/${draftId}`, { method: "DELETE" });
    if (res.ok) {
      toast.success("Brouillon supprimé");
      if (selected?.draft_id === draftId) setSelected(null);
      load();
    } else {
      toast.error("Erreur suppression");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="import-drafts-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded-2xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #7C3AED, #5B21B6)" }}
            >
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500">Sprint 5</div>
              <div className="font-bold text-base">Brouillons IA à valider</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5" data-testid="close-drafts-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : selected ? (
            <DraftDetail
              draft={selected}
              parcels={parcels}
              onBack={() => setSelected(null)}
              onValidated={() => {
                setSelected(null);
                load();
                if (onValidated) onValidated();
              }}
            />
          ) : drafts.length === 0 ? (
            <div className="p-10 text-center">
              <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500">Aucun brouillon en attente.</p>
              <p className="text-xs text-gray-400 mt-1">Importez un document pour commencer.</p>
            </div>
          ) : (
            <div className="p-4 space-y-2" data-testid="drafts-list">
              {drafts.map((d) => {
                const info = DOC_TYPE_LABELS[d.doc_type] || DOC_TYPE_LABELS.inconnu;
                const nbLines = d.extracted_lines?.length || 0;
                return (
                  <div
                    key={d.draft_id}
                    data-testid={`draft-row-${d.draft_id}`}
                    className="p-3 rounded-2xl border hover:shadow-md transition-shadow cursor-pointer flex items-center gap-3"
                    style={{ borderColor: info.color + "40", background: info.color + "08" }}
                    onClick={() => setSelected(d)}
                  >
                    <div className="text-2xl">{info.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{d.original_filename}</div>
                      <div className="text-xs text-gray-600 flex flex-wrap items-center gap-2 mt-0.5">
                        <span style={{ color: info.color }} className="font-medium">{info.label}</span>
                        <span>·</span>
                        <span>{nbLines > 0 ? `${nbLines} ligne${nbLines > 1 ? "s" : ""}` : (d.doc_type === "analyse_sol" ? "1 analyse" : "0 ligne")}</span>
                        <span>·</span>
                        <span>Confiance {(d.confidence * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(d.draft_id); }}
                      className="p-1.5 rounded-lg hover:bg-red-50"
                      data-testid={`delete-draft-${d.draft_id}`}
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function DraftDetail({ draft, parcels, onBack, onValidated }) {
  const info = DOC_TYPE_LABELS[draft.doc_type] || DOC_TYPE_LABELS.inconnu;
  const [lines, setLines] = useState(() => {
    if (draft.doc_type === "analyse_sol") {
      // Une seule "ligne synthétique" pour choisir la parcelle cible
      return [{
        line_index: 0,
        target_parcel_id: null,
        checked: true,
        _display: {
          product_name: "Analyse de sol",
          target_crop: null,
          quantity: null,
          unit: null,
        },
      }];
    }
    return (draft.extracted_lines || []).map((l, i) => ({
      line_index: i,
      target_parcel_id: null,
      application_date: l.application_date || (draft.meta?.invoice_date) || null,
      dose_ha: l.dose_ha,
      dose_unit: l.dose_unit,
      surface_ha: l.surface_ha,
      target_crop: l.target_crop,
      checked: true,
      _display: l,
    }));
  });
  const [saving, setSaving] = useState(false);

  const toggle = (i) => {
    setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, checked: !l.checked } : l));
  };
  const update = (i, key, value) => {
    setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, [key]: value } : l));
  };

  const handleValidate = async () => {
    const checked = lines.filter((l) => l.checked);
    if (checked.length === 0) {
      toast.error("Cochez au moins une ligne à valider");
      return;
    }
    if (draft.doc_type === "analyse_sol" && !checked[0].target_parcel_id) {
      toast.error("Sélectionnez la parcelle cible pour l'analyse de sol");
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch(`${API}/import/drafts/${draft.draft_id}/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "validate",
          lines: checked.map((l) => ({
            line_index: l.line_index,
            target_parcel_id: l.target_parcel_id,
            application_date: l.application_date,
            dose_ha: l.dose_ha ? Number(l.dose_ha) : null,
            dose_unit: l.dose_unit,
            surface_ha: l.surface_ha ? Number(l.surface_ha) : null,
            target_crop: l.target_crop,
            notes: l.notes,
          })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Erreur de validation");
        return;
      }
      const nbEntries = data.created_entries?.length || 0;
      if (draft.doc_type === "analyse_sol") {
        toast.success("Analyse de sol ajoutée au Carnet");
      } else {
        toast.success(`${nbEntries} entrée${nbEntries > 1 ? "s" : ""} créée${nbEntries > 1 ? "s" : ""} dans le Registre (en brouillon)`);
      }
      onValidated();
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm("Rejeter tout ce brouillon sans créer d'entrée ?")) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API}/import/drafts/${draft.draft_id}/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "reject", lines: [] }),
      });
      if (res.ok) {
        toast.success("Brouillon rejeté");
        onValidated();
      } else {
        toast.error("Erreur");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-5 space-y-4">
      <button onClick={onBack} className="text-xs text-gray-500 hover:text-gray-700" data-testid="back-to-drafts-list">
        ← Retour à la liste
      </button>

      <div className="p-4 rounded-2xl" style={{ background: info.color + "10", borderLeft: `4px solid ${info.color}` }}>
        <div className="text-xs uppercase tracking-widest" style={{ color: info.color }}>{info.icon} {info.label}</div>
        <div className="font-bold text-sm mt-1">{draft.original_filename}</div>
        {draft.meta && (
          <div className="text-xs text-gray-600 mt-2 space-y-0.5">
            {draft.meta.supplier && <div>Fournisseur : <strong>{draft.meta.supplier}</strong></div>}
            {draft.meta.invoice_date && <div>Date document : <strong>{draft.meta.invoice_date}</strong></div>}
            {draft.meta.invoice_number && <div>N° document : <strong>{draft.meta.invoice_number}</strong></div>}
            {draft.meta.operator_name && <div>Opérateur : <strong>{draft.meta.operator_name}</strong></div>}
          </div>
        )}
      </div>

      {draft.doc_type === "analyse_sol" ? (
        <div className="p-4 rounded-2xl border">
          <div className="text-sm font-medium mb-2">Paramètres extraits</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs mb-3">
            {Object.entries(draft.sol_data || {}).filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => (
              <div key={k} className="p-2 rounded-lg bg-gray-50">
                <div className="text-[10px] uppercase text-gray-500">{k}</div>
                <div className="font-medium">{String(v)}</div>
              </div>
            ))}
          </div>
          <label className="text-xs font-medium text-gray-700">Parcelle cible *</label>
          <select
            data-testid="analyse-sol-parcel-select"
            className="w-full mt-1 px-3 py-2 rounded-xl border text-sm"
            value={lines[0].target_parcel_id || ""}
            onChange={(e) => update(0, "target_parcel_id", e.target.value || null)}
          >
            <option value="">— Choisir une parcelle —</option>
            {parcels.map((p) => (
              <option key={p.parcel_id} value={p.parcel_id}>
                {p.name || p.parcel_id} ({p.culture_type || "—"})
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-sm font-medium">Lignes extraites ({lines.length})</div>
          {lines.map((l, i) => (
            <div
              key={i}
              data-testid={`draft-line-${i}`}
              className={`p-3 rounded-xl border transition-colors ${l.checked ? "bg-white" : "bg-gray-50 opacity-60"}`}
              style={{ borderColor: l.checked ? info.color + "60" : "#E5E7EB" }}
            >
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={l.checked}
                  onChange={() => toggle(i)}
                  className="mt-1"
                  data-testid={`draft-line-checkbox-${i}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">
                    {l._display.product_name || "(sans nom)"}
                    {l._display.amm_number && <span className="ml-2 text-[10px] text-gray-500">AMM {l._display.amm_number}</span>}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5">
                    {l._display.quantity && `${l._display.quantity} ${l._display.unit || ""}`}
                    {l._display.total_price_ht ? ` · ${l._display.total_price_ht} €` : ""}
                    {l._display.target_crop ? ` · ${l._display.target_crop}` : ""}
                  </div>

                  {l.checked && (
                    <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                      <div>
                        <label className="text-[10px] uppercase text-gray-500">Parcelle</label>
                        <select
                          data-testid={`line-parcel-${i}`}
                          className="w-full mt-0.5 px-2 py-1 rounded-lg border text-xs"
                          value={l.target_parcel_id || ""}
                          onChange={(e) => update(i, "target_parcel_id", e.target.value || null)}
                        >
                          <option value="">—</option>
                          {parcels.map((p) => (
                            <option key={p.parcel_id} value={p.parcel_id}>
                              {p.name || p.parcel_id}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase text-gray-500">Date application</label>
                        <input
                          data-testid={`line-date-${i}`}
                          type="date"
                          className="w-full mt-0.5 px-2 py-1 rounded-lg border text-xs"
                          value={l.application_date || ""}
                          onChange={(e) => update(i, "application_date", e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase text-gray-500">Dose /ha</label>
                        <input
                          data-testid={`line-dose-${i}`}
                          type="number"
                          step="0.01"
                          className="w-full mt-0.5 px-2 py-1 rounded-lg border text-xs"
                          value={l.dose_ha ?? ""}
                          onChange={(e) => update(i, "dose_ha", e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase text-gray-500">Surface ha</label>
                        <input
                          data-testid={`line-surface-${i}`}
                          type="number"
                          step="0.01"
                          className="w-full mt-0.5 px-2 py-1 rounded-lg border text-xs"
                          value={l.surface_ha ?? ""}
                          onChange={(e) => update(i, "surface_ha", e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="p-3 rounded-xl text-[11px] leading-relaxed" style={{ background: "#FEF3C7", color: "#92400E" }}>
        <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
        Les entrées sont créées <strong>en brouillon</strong>. Elles n&apos;entrent au registre qu&apos;après validation manuelle dans le Registre Phyto.
      </div>

      <div className="flex gap-2">
        <button
          data-testid="reject-draft-btn"
          onClick={handleReject}
          disabled={saving}
          className="flex-1 py-2.5 rounded-2xl text-sm font-medium border hover:bg-gray-50 disabled:opacity-50"
        >
          Rejeter
        </button>
        <button
          data-testid="validate-draft-btn"
          onClick={handleValidate}
          disabled={saving}
          className="flex-1 py-2.5 rounded-2xl text-white font-medium text-sm shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(135deg, #059669, #047857)" }}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Valider
        </button>
      </div>
    </div>
  );
}
